// src/services/db.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  runTransaction,
  writeBatch,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db, COL } from "../firebase";

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */

const snapToArray = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

/**
 * Codes are free text. An earlier version generated `LaBina-CL01` from a
 * number, which only ever fitted one supplier's pigments — needles and
 * cartridges use formats like `1201RLLT`, and the next brand will use
 * something else again. Read codes through these so products saved under
 * the old shape keep working.
 */
export const productCode = (p) => (p?.code || p?.pmuCode || "").trim();

export const productAltCode = (p) => (p?.codeAlt || p?.mbCode || "").trim();

/**
 * Either name may be blank — whichever exists is the product's name.
 * Never assume nameSq is present.
 */
export const displayName = (p, lang = "sq") => {
  const byLang = {
    sq: p?.nameSq,
    en: p?.nameEn,
    de: p?.nameDe,
  };
  // Fall back to Albanian, then to whatever exists, then to the code —
  // an untranslated product should still show its real name.
  return (
    byLang[lang]?.trim() ||
    p?.nameSq?.trim() ||
    p?.nameEn?.trim() ||
    p?.nameDe?.trim() ||
    p?.pmuCode ||
    ""
  );
};

/** Same rule for the long description. */
export const localizedDescription = (p, lang = "sq") => {
  const byLang = {
    sq: p?.description,
    en: p?.descriptionEn,
    de: p?.descriptionDe,
  };
  return (byLang[lang] || p?.description || "").trim();
};

/** Only returns a secondary name when both languages were actually filled. */
export const secondaryName = (p, lang = "sq") => {
  const primary = displayName(p, lang);
  const others = [p?.nameSq, p?.nameEn, p?.nameDe]
    .map((n) => n?.trim())
    .filter((n) => n && n !== primary);
  return others[0] || "";
};

/**
 * Products moved from a single `categoryId` to a `categoryIds` list.
 * Anything saved before that still has only the old field, so always read
 * categories through this — never off `product.categoryId` directly.
 */
export const productCategoryIds = (p) => {
  if (Array.isArray(p?.categoryIds) && p.categoryIds.length)
    return p.categoryIds;
  return p?.categoryId ? [p.categoryId] : [];
};

export const inCategory = (p, categoryId) =>
  !categoryId || productCategoryIds(p).includes(categoryId);

export const slugify = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/* ------------------------------------------------------------------ */
/*  categories                                                         */
/* ------------------------------------------------------------------ */

export const getCategories = async () => {
  const snap = await getDocs(
    query(collection(db, COL.categories), orderBy("order", "asc")),
  );
  return snapToArray(snap);
};

export class DuplicateCategoryError extends Error {
  constructor(nameSq) {
    super("DUPLICATE_CATEGORY");
    this.name = "DuplicateCategoryError";
    this.nameSq = nameSq;
  }
}

/**
 * The doc id is derived from the name so products keep matching even if
 * the display name is edited later. Returns the id so the caller can
 * select the category immediately.
 */
export const addCategory = async ({ nameSq, order = 99 }) => {
  const id = slugify(nameSq);
  if (!id) throw new Error("EMPTY_CATEGORY_NAME");

  const ref = doc(db, COL.categories, id);
  const existing = await getDoc(ref);
  if (existing.exists()) throw new DuplicateCategoryError(nameSq);

  await setDoc(ref, {
    nameSq: nameSq.trim(),
    order,
    createdAt: serverTimestamp(),
  });
  return id;
};

/** Turns a raw Firebase error into something an admin can act on. */
export const explainError = (err) => {
  if (err instanceof DuplicateCategoryError)
    return `"${err.nameSq}" ekziston tashmë.`;
  switch (err?.code) {
    case "permission-denied":
      return "Nuk keni leje për këtë veprim. Kontrolloni rregullat e Firestore (Rules) dhe që të jeni të kyçur.";
    case "unavailable":
    case "failed-precondition":
      return "Nuk ka lidhje me bazën e të dhënave. Kontrolloni internetin.";
    case "unauthenticated":
      return "Sesioni skadoi. Kyçuni përsëri.";
    default:
      return err?.message ? `Gabim: ${err.message}` : "Diçka shkoi keq.";
  }
};

export const updateCategory = (id, data) =>
  updateDoc(doc(db, COL.categories, id), data);

export const deleteCategory = (id) => deleteDoc(doc(db, COL.categories, id));

/* ------------------------------------------------------------------ */
/*  products                                                           */
/* ------------------------------------------------------------------ */

export const getProducts = async ({ categoryId } = {}) => {
  // Filtering happens client-side on purpose: an array-contains query would
  // miss every product saved before categoryIds existed, and an orderBy on
  // an optional field silently drops docs that lack it.
  const all = snapToArray(await getDocs(collection(db, COL.products)));
  return categoryId ? all.filter((p) => inCategory(p, categoryId)) : all;
};

export const getProduct = async (id) => {
  const snap = await getDoc(doc(db, COL.products, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const listenProducts = (cb) =>
  onSnapshot(collection(db, COL.products), (snap) => cb(snapToArray(snap)));

const normaliseProduct = (data) => {
  return {
    code: String(data.code ?? data.pmuCode ?? "").trim(),
    codeAlt: String(data.codeAlt ?? data.mbCode ?? "").trim(),
    // mirrored so anything still reading the old fields stays correct
    pmuCode: String(data.code ?? data.pmuCode ?? "").trim(),
    mbCode: String(data.codeAlt ?? data.mbCode ?? "").trim() || null,
    nameDe: data.nameDe?.trim() || "",
    nameEn: data.nameEn?.trim() || "",
    nameSq: data.nameSq?.trim() || "",
    slug: slugify(`${data.nameSq || data.nameDe || ""}-${data.code || ""}`),
    description: data.description?.trim() || "",
    descriptionEn: data.descriptionEn?.trim() || "",
    descriptionDe: data.descriptionDe?.trim() || "",
    hex: data.hex || null,
    categoryIds: Array.isArray(data.categoryIds)
      ? data.categoryIds.filter(Boolean)
      : data.categoryId
        ? [data.categoryId]
        : [],
    // kept in sync so any older code path reading the single field still works
    categoryId:
      (Array.isArray(data.categoryIds) && data.categoryIds[0]) ||
      data.categoryId ||
      "",
    onePigment: !!data.onePigment,
    isSample: !!data.isSample,
    price: Number(data.price) || 0,
    stock: Math.max(0, Number(data.stock) || 0),
    lowStockAt: Number(data.lowStockAt) || 3,
    images: data.images || [],
    active: data.active !== false,
  };
};

export const addProduct = (data) =>
  addDoc(collection(db, COL.products), {
    ...normaliseProduct(data),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const updateProduct = (id, data) =>
  updateDoc(doc(db, COL.products, id), {
    ...normaliseProduct(data),
    updatedAt: serverTimestamp(),
  });

export const deleteProduct = (id) => deleteDoc(doc(db, COL.products, id));

/** Adjust stock by a signed amount, e.g. +12 on a restock. */
export const adjustStock = (id, delta) =>
  updateDoc(doc(db, COL.products, id), {
    stock: increment(delta),
    updatedAt: serverTimestamp(),
  });

/** Bulk import. Firestore caps a batch at 500 writes, so chunk it. */
export const bulkAddProducts = async (rows) => {
  const chunks = [];
  for (let i = 0; i < rows.length; i += 400)
    chunks.push(rows.slice(i, i + 400));

  let written = 0;
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach((row) => {
      const ref = doc(collection(db, COL.products));
      batch.set(ref, {
        ...normaliseProduct(row),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
    written += chunk.length;
  }
  return written;
};

/* ------------------------------------------------------------------ */
/*  orders + stock                                                     */
/* ------------------------------------------------------------------ */

export class StockError extends Error {
  constructor(items) {
    super("INSUFFICIENT_STOCK");
    this.name = "StockError";
    this.items = items; // [{ id, nameSq, requested, available }]
  }
}

/**
 * Places an order and decrements stock atomically.
 *
 * Every read happens before every write — Firestore rejects a transaction
 * that reads after writing. If any line is short, nothing is written at all
 * and a StockError comes back listing exactly which items failed.
 */
export const placeOrder = async ({ customer, items, note = "" }) => {
  if (!items?.length) throw new Error("EMPTY_CART");

  const orderRef = doc(collection(db, COL.orders));

  await runTransaction(db, async (tx) => {
    // 1. read every product in the cart
    const snaps = await Promise.all(
      items.map((i) => tx.get(doc(db, COL.products, i.id))),
    );

    // 2. verify stock before touching anything
    const short = [];
    const lines = [];

    snaps.forEach((snap, idx) => {
      const item = items[idx];
      if (!snap.exists()) {
        short.push({
          id: item.id,
          nameSq: item.nameSq,
          requested: item.qty,
          available: 0,
        });
        return;
      }
      const p = snap.data();
      if (p.isSample) {
        short.push({
          id: item.id,
          nameSq: displayName(p),
          requested: item.qty,
          available: 0,
        });
        return;
      }
      const available = Number(p.stock) || 0;
      if (available < item.qty) {
        short.push({
          id: item.id,
          nameSq: displayName(p),
          requested: item.qty,
          available,
        });
        return;
      }
      lines.push({
        id: item.id,
        qty: item.qty,
        nameSq: displayName(p),
        pmuCode: productCode(p),
        hex: p.hex,
        price: Number(p.price) || 0,
        image: p.images?.[0] || null,
      });
    });

    if (short.length) throw new StockError(short);

    // 3. writes
    items.forEach((item) => {
      tx.update(doc(db, COL.products, item.id), {
        stock: increment(-item.qty),
        updatedAt: serverTimestamp(),
      });
    });

    const total = lines.reduce((sum, l) => sum + l.price * l.qty, 0);

    tx.set(orderRef, {
      customer,
      items: lines,
      total,
      note,
      status: "new", // new | confirmed | shipped | delivered | cancelled
      stockReturned: false,
      createdAt: serverTimestamp(),
    });
  });

  return orderRef.id;
};

/* ------------------------------------------------------------------ */
/*  sample requests                                                    */
/* ------------------------------------------------------------------ */

/**
 * Samples are given away, so they can't go through checkout — anyone would
 * take one. A request is captured instead and the client approves it by hand.
 * Stock is never touched here; nothing ships until a human says so.
 */
export const createSampleRequest = async ({ product, contact, note = "" }) => {
  const ref = await addDoc(collection(db, COL.sampleRequests), {
    product: {
      id: product.id,
      nameSq: displayName(product),
      pmuCode: productCode(product),
      hex: product.hex || "",
      image: product.images?.[0] || null,
    },
    contact,
    note,
    status: "new", // new | approved | sent | rejected
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const listenSampleRequests = (cb) =>
  onSnapshot(
    query(collection(db, COL.sampleRequests), orderBy("createdAt", "desc")),
    (snap) => cb(snapToArray(snap)),
  );

export const updateSampleRequestStatus = (id, status) =>
  updateDoc(doc(db, COL.sampleRequests, id), {
    status,
    updatedAt: serverTimestamp(),
  });

export const deleteSampleRequest = (id) =>
  deleteDoc(doc(db, COL.sampleRequests, id));

export const listenOrders = (cb) =>
  onSnapshot(
    query(collection(db, COL.orders), orderBy("createdAt", "desc")),
    (snap) => cb(snapToArray(snap)),
  );

/**
 * Changes an order's status. Cancelling returns the stock to the shelf,
 * guarded by stockReturned so a double-cancel can't inflate inventory.
 */
export const updateOrderStatus = async (orderId, status) => {
  const orderRef = doc(db, COL.orders, orderId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(orderRef);
    if (!snap.exists()) throw new Error("ORDER_NOT_FOUND");

    const order = snap.data();
    const cancelling = status === "cancelled" && !order.stockReturned;
    const reopening =
      order.status === "cancelled" &&
      status !== "cancelled" &&
      order.stockReturned;

    if (cancelling || reopening) {
      const sign = cancelling ? 1 : -1;
      order.items.forEach((line) => {
        tx.update(doc(db, COL.products, line.id), {
          stock: increment(sign * line.qty),
          updatedAt: serverTimestamp(),
        });
      });
    }

    tx.update(orderRef, {
      status,
      stockReturned: cancelling
        ? true
        : reopening
          ? false
          : order.stockReturned,
      updatedAt: serverTimestamp(),
    });
  });
};

export const deleteOrder = (id) => deleteDoc(doc(db, COL.orders, id));
